import { useMemo, type ReactNode } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  useGallery,
  usePropertyField,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../../utils/heroImageUtils';

/** Inner-page hero title: shared scale/width behavior; keep normal weight. */
export const INNER_PAGE_HERO_TITLE_CLASS =
  'font-heading text-2xl sm:text-3xl lg:text-4xl font-normal uppercase tracking-wider leading-tight break-words mt-3 md:mt-4';

export const INNER_PAGE_HERO_SUBTITLE_CLASS =
  'font-body text-base sm:text-lg md:text-xl max-w-2xl leading-relaxed font-normal opacity-90 mt-1 text-pretty text-balance';

interface InnerPageHeroSectionProps {
  bgGroupPath: string;
  bgImageDef: SlotGroupDefinition;
  altFallback: string;
  children: ReactNode;
  /** Override for gallery assignment; defaults to bgGroupPath without `.bg_image`. */
  pageHint?: string;
}

export function InnerPageHeroSection({
  bgGroupPath,
  bgImageDef,
  altFallback,
  children,
  pageHint,
}: InnerPageHeroSectionProps) {
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const resolvedPageHint =
    pageHint || bgGroupPath.replace(/\.bg_image$/, '');

  // Unique page-aware gallery pick (unit/staged for floor plans, pool for
  // amenities, etc.). Returns -1 for featured image when this page should
  // yield its gallery slot (utility pages, or no unused desirable photo left).
  const photoIndex = useMemo(
    () => pickHeroImage(gallery, resolvedPageHint),
    [gallery, resolvedPageHint],
  );

  // Runtime defaults point at the picked gallery photo so useSlotGroup
  // resolves it unless an editor override is present. Schema stays on
  // property_featured_image for static lint / editor fallback.
  const runtimeBgDef = useMemo((): SlotGroupDefinition => {
    if (photoIndex < 0) return bgImageDef;
    return {
      ...bgImageDef,
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: `photo_gallery[${photoIndex}].image`,
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: `photo_gallery[${photoIndex}].alt_text`,
          defaultValue: altFallback,
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: photoIndex,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    };
  }, [photoIndex, bgImageDef, altFallback]);

  const bgImage = useSlotGroup(bgGroupPath, runtimeBgDef);

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0
      ? gallery[photoIndex]?.image || gallery[photoIndex]?.original_url
      : null) ||
    featuredImage ||
    gallery[0]?.image ||
    gallery[0]?.original_url ||
    null;

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup
        groupPath={bgGroupPath}
        definition={bgImageDef}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            url={displayImage}
            alt={bgImage.alt || altFallback}
            className="w-full h-full object-cover"
            sizes="100vw"
          />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/25" />
      </EditableSlotGroup>

      <div
        className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4"
        style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}
      >
        {children}
      </div>
    </section>
  );
}
